import Accordion from 'components/Accordion';
import ApplicationActivityList from 'components/ApplicationActivityList';
import DetailsViewCardHeader from 'components/DetailsViewCardHeader';
import DetailsViewEntityMenu from 'components/DetailsViewEntityMenu';
import DetailsViewPageContainer from 'components/DetailsViewPageContainer';
import LinkedEntitiesAccordion from 'components/LinkedEntitiesAccordion';
import UpdateApplicationForm from 'components/forms/UpdateApplicationForm';

import useData from './hooks/useData';
import useTranslation from './hooks/useTranslation';

import {
    CARD_HEADER_DETAIL_TYPE,
    DETAIL_TYPE_BATTERY,
    DETAIL_TYPE_DRONE,
    DETAIL_TYPE_PILOT,
    DETAIL_TYPE_SCHEDULED_PILOT,
    TEST_ID
} from './constants';
import type { FunctionComponent, Props } from './types';

const ApplicationDetailsPage: FunctionComponent<Props> = ({ testId = TEST_ID }) => {
    const {
        batteryMenuItems,
        currentApplication,
        droneMenuItems,
        dropdownMenuItems,
        entitiesLink,
        flights,
        geometry,
        handleClose,
        handleEditClose,
        handleEditMapClick,
        handleEditMapClose,
        handleMapComplete,
        handleNextFlightsPage,
        handleSelectFlight,
        handleToggleActivity,
        handleToggleLinkedEntities,
        hasMoreFlights,
        isActivityOpen,
        isApplicationLoading,
        isClosing,
        isEditingForm,
        isEditingMap,
        isLinkedEntitiesOpen,
        mapInteractionOptions,
        mapOptions,
        pilotMenuItems,
        scheduledPilotMenuItems,
        selectedFlight,
        setIsClosing
    } = useData();

    const { ACTIVITY, LINKED_ENTITIES } = useTranslation();

    const { hasDrawingControls } = mapInteractionOptions;

    return (
        <DetailsViewPageContainer
            flights={flights}
            geometry={geometry}
            handleClose={handleClose}
            handleEditMapClick={handleEditMapClick}
            handleEditMapClose={handleEditMapClose}
            handleMapComplete={handleMapComplete}
            hasDrawingControls={hasDrawingControls}
            mapInteractionOptions={mapInteractionOptions}
            mapOptions={mapOptions}
            selectedFlight={selectedFlight}
            testId={testId}>
            <DetailsViewCardHeader
                detailType={CARD_HEADER_DETAIL_TYPE}
                isDisabled={isEditingForm || isEditingMap}
                isLoading={isApplicationLoading}
                items={dropdownMenuItems}
                name={currentApplication?.name ?? ''}
            />

            {isEditingForm ? (
                <UpdateApplicationForm
                    formValues={currentApplication}
                    isClosing={isClosing}
                    onClose={handleEditClose}
                    setIsClosing={setIsClosing}
                />
            ) : (
                <>
                    <LinkedEntitiesAccordion
                        entitiesLink={entitiesLink}
                        isOpen={isLinkedEntitiesOpen}
                        label={LINKED_ENTITIES}
                        onToggle={handleToggleLinkedEntities}>
                        <DetailsViewEntityMenu
                            entityType={DETAIL_TYPE_DRONE}
                            isLoading={isApplicationLoading}
                            items={droneMenuItems}
                        />

                        <DetailsViewEntityMenu
                            entityType={DETAIL_TYPE_BATTERY}
                            isLoading={isApplicationLoading}
                            items={batteryMenuItems}
                        />

                        <DetailsViewEntityMenu
                            entityType={DETAIL_TYPE_SCHEDULED_PILOT}
                            isLoading={isApplicationLoading}
                            items={scheduledPilotMenuItems}
                        />

                        <DetailsViewEntityMenu
                            entityType={DETAIL_TYPE_PILOT}
                            isLoading={isApplicationLoading}
                            items={pilotMenuItems}
                        />
                    </LinkedEntitiesAccordion>

                    <Accordion
                        isOpen={isActivityOpen}
                        label={ACTIVITY}
                        onToggle={handleToggleActivity}>
                        <ApplicationActivityList
                            applicationStatus={currentApplication?.status}
                            fetchNextPage={handleNextFlightsPage}
                            flights={flights}
                            hasNextPage={hasMoreFlights}
                            isLoading={isApplicationLoading}
                            onSelectFlight={handleSelectFlight}
                            selectedFlightId={selectedFlight?.id}
                        />
                    </Accordion>
                </>
            )}
        </DetailsViewPageContainer>
    );
};

export default ApplicationDetailsPage;
